import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { VerifiedUser, Science } from '@mui/icons-material';
import { AdminUserSubscription } from '@services/admin-users.service';

interface PlanChipProps {
  subscription?: AdminUserSubscription | null;
  // Опційно: якщо передано (навіть false) — показує іконку "чесна оплата".
  // Досі undefined на сторінках, які не прокидають provider/grantedBy у
  // subscription (experts, all-users, finance) — там іконка не рендериться,
  // щоб не показувати хибне "нечесно" через відсутність даних.
  billingExempt?: boolean;
}

const PLAN_CONFIG: Record<string, { label: string; color: 'warning' | 'default' }> = {
  pro_tier:     { label: 'Pro',              color: 'warning' },
  default_tier: { label: 'За замовчуванням', color: 'default' },
};

const PROVIDER_LABELS: Record<string, string> = {
  wayforpay: 'WayForPay',
  apple: 'Apple IAP',
  google: 'Google Play',
  virtual: 'тестова (virtual)',
};

// "Чесна" оплата — реальний провайдер (не virtual-емуляція) і не адмінський
// override, без billingExempt. Введено 2026-08-11 для аудиту тестових
// акаунтів після кількох міграцій монетизації.
function isHonestPayment(subscription: AdminUserSubscription | null | undefined, billingExempt?: boolean): boolean {
  if (billingExempt || !subscription) return false;
  return subscription.grantedBy === 'payment' && subscription.provider !== 'virtual';
}

export const PlanChip: React.FC<PlanChipProps> = ({ subscription, billingExempt }) => {
  if (!subscription) {
    return <Typography variant="body2" color="text.disabled">—</Typography>;
  }

  const cfg = PLAN_CONFIG[subscription.planCode] ?? { label: subscription.planName, color: 'default' as const };
  const showHonestyIndicator = billingExempt !== undefined;
  const honest = isHonestPayment(subscription, billingExempt);

  const tooltip = billingExempt
    ? 'billingExempt: білінг вимкнено для акаунту, оплата не перевіряється'
    : honest
      ? `Чесна оплата: ${PROVIDER_LABELS[subscription.provider ?? ''] ?? subscription.provider}`
      : subscription.grantedBy === 'admin_grant'
        ? 'Адмінський override плану (без реальної оплати)'
        : `Тестова оплата (${PROVIDER_LABELS[subscription.provider ?? ''] ?? subscription.provider ?? 'невідомо'})`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Chip
        label={cfg.label}
        size="small"
        color={cfg.color}
        variant={cfg.color === 'warning' ? 'filled' : 'outlined'}
      />
      {showHonestyIndicator && (
        <Tooltip title={tooltip}>
          {honest
            ? <VerifiedUser color="success" sx={{ fontSize: 15 }} />
            : <Science color="disabled" sx={{ fontSize: 15 }} />}
        </Tooltip>
      )}
    </Box>
  );
};
